export const DEFAULT_EVENT_IMAGE = "/img/cover/cover_calendar.jpeg";

export const EVENT_TYPES = [
  "Animation",
  "Compétition",
  "Stage",
  "Tournoi Interclub",
  "UV1 Kata",
  "UV2 Technique",
  "UV3 Shiai",
  "UV4 Arbitrage",
];

export const EVENT_CATEGORIES = [
  "Eveils",
  "Mini-Poussins",
  "Poussins",
  "Benjamins",
  "Minimes",
  "Cadets",
  "Juniors",
  "Séniors",
  "Vétérans",
];

export const EVENT_LEVELS = [
  "Club",
  "Départemental",
  "Régional",
  "National",
  "International",
];

export const EMPTY_EVENT_FORM = {
  titre: "",
  description: "",
  start: "",
  type_event: "",
  cate_event: "",
  level_event: "",
  lieu_event: "",
  horaire_event: "",
  photo_url: "",
  is_shared: false,
};

export const DEFAULT_FILTERS = {
  type_event: "",
  cate_event: "",
  level_event: "",
  time_event: "upcoming",
};

export const normalizeAdherentsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.adherents)) return response.data.adherents;
  return [];
};

export const normalizeEventsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Events)) return response.Events;
  if (Array.isArray(response?.events)) return response.events;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.Events)) return response.data.Events;
  if (Array.isArray(response?.data?.events)) return response.data.events;
  return [];
};

export const normalizeCompetitorsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.adherents)) return response.adherents;
  if (Array.isArray(response?.Adherents)) return response.Adherents;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const normalizeDateForInput = (dateValue) => {
  if (!dateValue) return "";

  const date = new Date(dateValue);
  if (!Number.isFinite(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
};

export const normalizeEventForForm = (event = {}) => ({
  ...EMPTY_EVENT_FORM,
  ...event,
  start: normalizeDateForInput(event.start),
  is_shared: Boolean(event.is_shared),
  cate_event: Array.isArray(event.cate_event)
    ? event.cate_event.join(",")
    : event.cate_event || "",
});

export const formatEventDate = (dateValue) => {
  if (!dateValue) return "Date non renseignée";

  const date = new Date(dateValue);
  if (!Number.isFinite(date.getTime())) return "Date non renseignée";

  return date.toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

export const formatEventDay = (dateValue) => {
  const date = new Date(dateValue);
  if (!Number.isFinite(date.getTime())) return "--";

  return date.toLocaleDateString("fr-FR", {
    day: "2-digit",
  });
};

export const formatEventMonth = (dateValue) => {
  const date = new Date(dateValue);
  if (!Number.isFinite(date.getTime())) return "---";

  return date.toLocaleDateString("fr-FR", {
    month: "short",
  });
};

export const formatEventYear = (dateValue) => {
  const date = new Date(dateValue);
  if (!Number.isFinite(date.getTime())) return "";

  return date.getFullYear();
};

export const isPastEvent = (event) => {
  if (!event?.start) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const eventDate = new Date(event.start);
  eventDate.setHours(0, 0, 0, 0);

  return eventDate < today;
};

export const splitEventsByTime = (events = []) => {
  const sortedEvents = [...events].sort(
    (a, b) => new Date(a.start) - new Date(b.start)
  );

  return {
    past: sortedEvents.filter(isPastEvent),
    upcoming: sortedEvents.filter((event) => !isPastEvent(event)),
  };
};

export const getFilteredEvents = (eventsByTime, filters) => {
  const sourceEvents =
    filters.time_event === "all"
      ? [...(eventsByTime.past || []), ...(eventsByTime.upcoming || [])]
      : filters.time_event === "past"
      ? eventsByTime.past || []
      : eventsByTime.upcoming || [];

  return sourceEvents.filter((event) => {
    const matchType = !filters.type_event || event.type_event === filters.type_event;

    const eventCategories = String(event.cate_event || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

    const matchCategory =
      !filters.cate_event || eventCategories.includes(filters.cate_event);

    const matchLevel =
      !filters.level_event || event.level_event === filters.level_event;

    return matchType && matchCategory && matchLevel;
  });
};

export const hasEventResults = (event) =>
  Boolean(
    event?.AdherentEvent?.resultats ||
      event?.AdherentEvent?.classement ||
      event?.AdherentEvent?.observations ||
      event?.AdherentEvent?.podium_url ||
      event?.AdherentEvent?.tableau_url
  );

export const getEventAdherentId = (event) =>
  event?.AdherentEvent?.adherent_id ||
  event?.AdherentEvent?.adherentId ||
  null;

export const isEventLinkedToSelectedAdherent = (event, adherentId) => {
  const linkedAdherentId = getEventAdherentId(event);

  return Boolean(
    adherentId &&
      linkedAdherentId &&
      Number(linkedAdherentId) === Number(adherentId),
  );
};

/**
 * Ancienne logique de ton app :
 * les événements créés par un judoka ont parent_event_id = adherentId.
 *
 * On la garde pour ne pas casser les événements existants.
 */
export const isLegacyOwnCreatedEvent = (event, adherentId) =>
  Boolean(
    event?.parent_event_id &&
      adherentId &&
      Number(event.parent_event_id) === Number(adherentId),
  );

/**
 * Modification :
 * On autorise seulement les événements créés par le judoka selon ton ancienne logique.
 * Une copie d'événement partagé ne doit pas forcément être modifiable.
 */
export const canEditEvent = (event, adherentId) =>
  isLegacyOwnCreatedEvent(event, adherentId);

/**
 * Suppression :
 * On autorise la suppression si :
 * - l'événement appartient à l'adhérent sélectionné via AdherentEvent
 * - l'événement n'est pas partagé
 *
 * Cela permet de supprimer :
 * - les événements créés par le judoka
 * - les copies personnelles d'événements partagés
 * - les anciennes copies avec parent_event_id null
 */
export const canDeleteEvent = (event, adherentId) =>
  Boolean(
    !event?.is_shared && isEventLinkedToSelectedAdherent(event, adherentId),
  );

/**
 * Tu peux garder canManageEvent en alias pour éviter de casser d'autres imports.
 */
export const canManageEvent = canEditEvent;

export const canInviteToEvent = (user) =>
  Boolean(user?.role_id && [1, 5, 6, 7].includes(user.role_id));

export const canViewCompetitors = (user) =>
  Boolean(user?.role_id && [5, 6, 7].includes(user.role_id));

export const getStatusLabel = (status) => {
  if (status === "pending") return "En attente";
  if (status === "accepted") return "Inscrit";
  if (status === "rejected") return "Refusé";
  return "Statut inconnu";
};

export const getStatusStyle = (status) => {
  if (status === "pending") {
    return "bg-orange-100 text-orange-700 dark:bg-orange-500/10 dark:text-orange-200";
  }

  if (status === "accepted") {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200";
  }

  if (status === "rejected") {
    return "bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200";
  }

  return "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-white/50";
};

export const formatCompetitorLabel = (adherent) => {
  const name = [String(adherent?.nom || "").toUpperCase(), adherent?.prenom]
    .filter(Boolean)
    .join(" ");

  return adherent?.poids ? `${name} (${adherent.poids} kg)` : name;
};